import React, { useEffect, useState } from "react";
import { collection, deleteDoc, deleteField, doc, getDocs, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { ExternalLink, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { db, storage } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";

const AdminCertificates = () => {
  const { profile } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [certMap, setCertMap] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [studSnap, certSnap] = await Promise.all([
        getDocs(collection(db, "students")),
        getDocs(collection(db, "certificates")),
      ]);
      const studs = studSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) =>
        ["paid", "free_via_coupon", "success"].includes(s.paymentStatus) || Number(s.amountPaid) === 0
      );
      const cMap = {};
      certSnap.docs.forEach((d) => {
        const data = d.data();
        cMap[data.studentId] = { id: d.id, ...data };
      });
      setStudents(studs);
      setCertMap(cMap);
    } catch (error) {
      toast.error(error.message || "Unable to load certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadCertificate = async (student, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }

    setUploading(student.studentId);
    try {
      const existing = certMap[student.studentId];
      const certId = existing?.certificateId || `KC-${student.studentId}`;
      const safeCertFileId = certId.replace(/\//g, "-");
      const storageRef = ref(storage, `certificates/${safeCertFileId}.pdf`);

      await uploadBytes(storageRef, file, { contentType: "application/pdf" });
      const certificateUrl = await getDownloadURL(storageRef);

      await setDoc(doc(db, "certificates", safeCertFileId), {
        certificateId: certId,
        safeCertFileId,
        studentId: student.studentId,
        studentName: student.fullName,
        studentRollNumber: student.universityRollNumber || student.rollNumber || "",
        studentSubject: student.subjectName || student.majorSubject || "",
        studentCollege: student.college || "",
        studentSession: student.academicSession || "",
        studentSemester: student.classSemester || "",
        courseTitle: student.selectedInternship || "",
        duration: "120 Hours",
        attendancePct: student.attendancePercentage || 0,
        issueDate: existing?.issueDate || new Date().toLocaleDateString("en-IN"),
        certificateUrl,
        status: "valid",
        issuedBy: "Kampus Campanion",
        uploadedManually: true,
        manualPdfUploadedAt: serverTimestamp(),
        createdAt: existing?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await updateDoc(doc(db, "students", student.studentId), {
        certificateStatus: "issued",
        certificateId: certId,
        certificateDocId: safeCertFileId,
      });

      toast.success("Certificate PDF uploaded");
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Certificate upload failed");
    } finally {
      setUploading(null);
    }
  };

  const deleteCertificate = async (student, cert) => {
    if (!cert) return;
    if (!window.confirm(`Delete certificate PDF for ${student.fullName}?`)) return;

    setDeleting(student.studentId);
    try {
      const certDocId = cert.safeCertFileId || cert.id;
      const storageFileId = cert.safeCertFileId || cert.certificateId?.replace(/\//g, "-") || cert.id;

      if (storageFileId) {
        try {
          await deleteObject(ref(storage, `certificates/${storageFileId}.pdf`));
        } catch (error) {
          console.warn("Certificate PDF delete skipped:", error?.message);
        }
      }

      await deleteDoc(doc(db, "certificates", certDocId));
      await updateDoc(doc(db, "students", student.studentId), {
        certificateStatus: "not_issued",
        certificateId: deleteField(),
        certificateDocId: deleteField(),
      });

      toast.success("Certificate removed");
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Unable to delete certificate");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="admin-certificates">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Certificate Management</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Certificates</h1>
        <p className="mt-1 text-slate-600">Upload certificate PDFs manually and keep them verifiable with unique IDs.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : students.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="no-students-cert">No eligible students yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Student</th>
                <th className="text-left px-6 py-3 font-semibold">Course</th>
                <th className="text-left px-6 py-3 font-semibold">Attendance</th>
                <th className="text-left px-6 py-3 font-semibold">Cert. Status</th>
                <th className="text-left px-6 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => {
                const cert = certMap[student.studentId];
                const manualCert = Boolean(cert?.manualPdfUploadedAt);
                const eligible = (student.attendancePercentage || 0) >= 75;
                return (
                  <tr key={student.id} className="border-t border-slate-100">
                    <td className="px-6 py-3">
                      <div className="font-medium text-slate-900">{student.fullName}</div>
                      <div className="text-xs text-slate-500 font-mono">{student.studentId}</div>
                    </td>
                    <td className="px-6 py-3 text-slate-700">{student.selectedInternship}</td>
                    <td className="px-6 py-3">
                      <span className={eligible ? "text-green-700 font-semibold" : "text-amber-700"}>
                        {student.attendancePercentage || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      {manualCert ? (
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Issued</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">Not issued</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {manualCert ? (
                        <div className="flex items-center gap-3">
                          <a href={cert.certificateUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-semibold hover:underline inline-flex items-center gap-1">
                            View PDF <ExternalLink className="w-3 h-3" />
                          </a>
                          <UploadButton
                            student={student}
                            uploading={uploading}
                            onUpload={uploadCertificate}
                            label={profile?.hasControls ? "Edit Upload PDF" : "Replace PDF"}
                            variant="link"
                          />
                          <button
                            type="button"
                            onClick={() => deleteCertificate(student, cert)}
                            disabled={deleting === student.studentId}
                            className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1 disabled:opacity-50"
                            title="Delete certificate"
                          >
                            <Trash2 className="w-3 h-3" />
                            {deleting === student.studentId ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      ) : (
                        <UploadButton
                          student={student}
                          uploading={uploading}
                          onUpload={uploadCertificate}
                          label="Upload PDF"
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const UploadButton = ({ student, uploading, onUpload, label, variant = "button" }) => {
  const inputId = `upload-cert-${student.studentId}`;
  const busy = uploading === student.studentId;

  return (
    <>
      <input
        id={inputId}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          onUpload(student, file);
          event.target.value = "";
        }}
      />
      <label
        htmlFor={inputId}
        data-testid={`upload-cert-${student.studentId}`}
        className={
          variant === "link"
            ? `text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 cursor-pointer ${busy ? "pointer-events-none opacity-50" : ""}`
            : `inline-flex cursor-pointer items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 ${busy ? "pointer-events-none opacity-50" : ""}`
        }
      >
        <Upload className="w-3 h-3" />
        {busy ? "Uploading..." : label}
      </label>
    </>
  );
};

export default AdminCertificates;

